import ReactDOM from 'react-dom'
import React, { Suspense, useState, useRef, useEffect, useMemo } from 'react'
import { Canvas, useFrame, useLoader, useUpdate, useThree } from 'react-three-fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { HTML } from 'drei'
import boo from './assets/boo/untitled.glb'
import smootharroww from './assets/symmetric_block_arrow.glb'
import roomrect from './assets/roomsphere.glb'
import cow from './assets/cowcropped.gif'
// import { useSpring, useTransition, animated, Transition } from '@react-spring/core'
// import { a } from '@react-spring/three'
import sunset from './assets/sunset-min.png'
import * as THREE from 'three'
import { useScreenSize, deviceMap } from './screenSize'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Paintings } from './paintings'

import './styles.css'

const Link = ({ href, text, noTarget = false }) => {
  const size = useScreenSize()
  const isDesktop = size.device === deviceMap.desktop
  return (
    <span style={{ cursor: 'pointer' }}>
      <a style={{ color: isDesktop ? '#0080ff' : '#3e009c' }} href={href} {...(noTarget ? {} : { target: '_blank' })}>
        {text}
      </a>
    </span>
  )
}

const Owesy = ({ textStyle }) => {
  return (
    <div style={{ maxWidth: 500 }}>
      <span style={textStyle}>
        Made <Link href={'http://owesy.app'} text={'Owesy'} />. Quickly calculate how to equally split costs with friends despite uneven
        paid amounts. Available on <Link href={'https://apps.apple.com/us/app/owesy/id1445506292'} text={'iOS'} /> and{' '}
        <Link href={'https://play.google.com/store/apps/details?id=com.jacobloeterman.owesy&hl=en_US'} text={'Android'} />.
      </span>
    </div>
  )
}

const AboutMeText = ({ shouldHighlight }) => {
  const textStyle = { ...(shouldHighlight ? { backgroundColor: '#039dfc', opacity: 0.5, color: 'black' } : {}) }
  return (
    <>
      {/* <div style={{ color: '#ef573d', marginBottom: 10, marginTop: -80 }}>A quiet place to round up some Internet things...</div> */}
      <div style={{ marginTop: -80, ...(shouldHighlight ? { color: '#f55742', maxWidth: 320 } : {}) }}>
        <div style={{ marginBottom: 20, maxWidth: 430 }}>
          <span style={textStyle}>
            Currently at <Link text={'STRIVR'} href={'https://www.strivr.com/'} />. Previously at{' '}
            <Link text={'IrisVR'} href={'https://irisvr.com/'} /> working on web, PC, and Oculus Quest apps. Before that,{' '}
            <Link text={'Kickstarter'} href={'https://kickstarter.com/'} />.
          </span>
        </div>
        <div style={{ marginBottom: 20 }}>
          <Owesy textStyle={textStyle} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>
            <span style={textStyle}>
              Some of my <Link text={'3d scanned paintings are here'} href={'/art'} noTarget />.
            </span>
          </div>
          <div>
            <span style={textStyle}>
              <Link text={'jloe.work'} href={'http://jloe.work'} /> has some paintings and is best viewed in a Safari or VR-enabled browser.
            </span>
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <span style={textStyle}>
            <Link text={'Coool Cow Masks'} href={'https://www.instagram.com/ar/1671541053011681/'} /> is an Instagram effect of various
            painted cows.
          </span>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div>
            <span style={textStyle}>
              I was part of a WebVR interview on Glitch{' '}
              <Link
                text={'a while ago'}
                href={'https://glitch.com/glimmer/post/learn-to-create-vr-experiences-with-the-webvr-starter-kit'}
              />
              .
            </span>
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <span style={textStyle}>
            My all but defunct Etsy store <Link text={'ItsCowadise'} href={'https://www.etsy.com/uk/shop/ItsCowadise'} /> sometimes has a
            shirt or hoodie.
          </span>
        </div>
        <span style={textStyle}>jaloeterman at gmail dot com</span>
        <div>
          <span style={textStyle}>
            <Link text={'@jakeloet'} href={'https://twitter.com/jakeloet'} /> on twitter
          </span>
        </div>
        <div>
          <span style={textStyle}>
            <Link text={'@darkpano'} href={'https://www.instagram.com/darkpano/'} /> on insta
          </span>
        </div>
      </div>
    </>
  )
}

const Webpage = () => {
  const { camera } = useThree()
  useEffect(() => {
    camera.position.x = -10
    camera.position.z = -10
    camera.position.y = 12
  }, [])

  const size = useScreenSize()

  const isDesktop = size.device === deviceMap.desktop
  const isMobile = size.device === deviceMap.mobile
  return (
    <HTML>
      <div style={{ background: 'white', color: '#333333' }}>
        {isDesktop && (
          <div style={{ display: 'flex' }}>
            <Suspense fallback={null}>
              <div style={{ position: 'absolute', left: -750, top: -500 }}>
                <img style={{ flex: 1, width: 756, height: 1008, resizeMode: 'contain' }} src={sunset} alt="supp" />
              </div>
            </Suspense>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ marginLeft: 40, marginBottom: 50, marginTop: -280 }}>
                <img style={{ height: 240, width: 500, marginTop: -50, marginBottom: 70 }} src={cow} alt="cow..." />
              </div>
              <div style={{ marginLeft: 20 }}>
                <AboutMeText shouldHighlight={isMobile} />
              </div>
            </div>
          </div>
        )}
        {isMobile && (
          <div>
            <Suspense fallback={null}>
              <div style={{ position: 'absolute', left: -436, top: -500 }}>
                <img style={{ flex: 1, width: 756, height: 1008, resizeMode: 'contain' }} src={sunset} alt="supp" />
              </div>
            </Suspense>
            <div style={{ position: 'absolute', left: -153, top: -129, width: 300 }}>
              <AboutMeText shouldHighlight={isMobile} />
            </div>
            <div style={{ position: 'absolute', left: 58, top: -255 }}>
              <img style={{ height: 60, width: 125, marginTop: -50, marginBottom: 70 }} src={cow} alt="cow..." />
            </div>
          </div>
        )}
      </div>
    </HTML>
  )
}

const Father = () => {
  const [isBooActivated, setActivateBoo] = useState(false)
  const [showNewPage, setShowNewPage] = useState(false)
  const size = useScreenSize()
  const isDesktop = size.device === deviceMap.desktop
  const timeoutNumber = isDesktop ? 4300 : 4800
  const { camera } = useThree()

  useEffect(() => {
    if (isBooActivated) {
      setTimeout(() => {
        setShowNewPage(true)
      }, timeoutNumber)
    }
  }, [isBooActivated, timeoutNumber])

  useFrame(() => {
    if (isBooActivated && !showNewPage && isDesktop) {
      camera.position.z = camera.position.z += 0.14
      camera.position.y = camera.position.y -= 0.097
      camera.position.x = camera.position.x += 0.13
    }
  })

  if (showNewPage) {
    // return (
    //   <Transition items={[<Webpage />]} from={{ opacity: 0 }} enter={{ opacity: 1 }}>
    //     {(values, item) => <animated.div style={values}>{item}</animated.div>}
    //   </Transition>
    // )
    return <Webpage />
  }

  return (
    <mesh onClick={() => setActivateBoo(true)}>
      {isDesktop && (
        <Suspense fallback={null}>
          <RoomRect />
        </Suspense>
      )}
      <Box isBooActivated={isBooActivated} />
      {!isBooActivated && (
        <Suspense fallback={null}>
          <Arrow />
        </Suspense>
      )}
      {isBooActivated && (
        <Suspense fallback={null}>
          <Boo />
        </Suspense>
      )}
    </mesh>
  )
}

function Boo() {
  const mesh = useRef()
  useFrame(() => (mesh.current.rotation.x = mesh.current.rotation.y -= 0.001))
  useFrame(() => (mesh.current.position.z = mesh.current.position.y += 1))

  const gltf = useLoader(GLTFLoader, boo)
  return (
    <mesh ref={mesh}>
      <primitive object={gltf.scene} position={[600, -700, 0]} rotation={[3, 5, 8]} />
    </mesh>
  )
}

function Arrow() {
  const ref = useRef()
  // useFrame(() => (mesh.current.rotation.x = mesh.current.rotation.y -= 0.001))
  // useFrame(() => (mesh.current.position.z = mesh.current.position.y += 1))

  useFrame(({ clock }) => {
    // ref.current.rotation.x = ref.current.rotation.y = ref.current.rotation.z = Math.cos(clock.getElapsedTime()) * 0.1
    ref.current.position.y = ref.current.position.z = ref.current.position.z = -Math.abs(Math.cos(clock.getElapsedTime()) * 1.4)
    ref.current.scale.y = ref.current.position.y = ref.current.position.y = -Math.abs(Math.cos(clock.getElapsedTime()) * 0.12)
    ref.current.scale.x = ref.current.position.x = ref.current.position.x = -Math.abs(Math.cos(clock.getElapsedTime()) * 0.12)
    ref.current.scale.z = ref.current.position.z = ref.current.position.z = -Math.abs(Math.cos(clock.getElapsedTime()) * 0.12)
  })

  const gltf = useLoader(GLTFLoader, smootharroww)
  return (
    <mesh ref={ref}>
      <primitive object={gltf.scene} scale={[3.3, 3.3, 3.3]} position={[20, -20, 11]} rotation={[6, 3, 9]} />
    </mesh>
  )
}

function RoomRect() {
  const gltf = useLoader(GLTFLoader, roomrect)
  return <primitive object={gltf.scene} scale={[2, 2, 2]} position={[22, -12, 25]} rotation={[-20, -2, 22]} />
}

const Text = ({ children, vAlign = 'center', hAlign = 'center', size = 1, color = '#000000', ...props }) => {
  const font = useLoader(THREE.FontLoader, '/bold.blob')
  const config = useMemo(
    () => ({
      font,
      size: 40,
      height: 30,
      curveSegments: 35,
      bevelEnabled: true,
      bevelThickness: 1,
      bevelSize: 1,
      bevelOffset: 0,
      bevelSegments: 1
    }),
    [font]
  )
  const mesh = useUpdate(
    (self) => {
      const size = new THREE.Vector3()
      self.geometry.computeBoundingBox()
      self.geometry.boundingBox.getSize(size)
      self.position.x = hAlign === 'center' ? -size.x / 2 : hAlign === 'right' ? 0 : -size.x
      self.position.y = vAlign === 'center' ? -size.y / 2 : vAlign === 'top' ? 0 : -size.y
    },
    [children]
  )
  return (
    <group {...props} scale={[0.07 * size, 0.05 * size, 0.07]}>
      <mesh ref={mesh}>
        <textGeometry attach="geometry" args={[children, config]} />
        <meshNormalMaterial attach="material" />
      </mesh>
    </group>
  )
}

function Box(props) {
  const mesh = useRef()
  const { isBooActivated } = props
  // const newColors = ['#42e0f5', '#42b0f5', '#427ef5']
  // let [count, setCount] = useState(0)
  // const randomNumber = () => Math.floor(Math.random() * newColors.length)
  //let randomColor = newColors[randomNumber()]

  // useInterval(() => {
  //   // Your custom logic here
  //   setCount(count + 1)
  //   randomColor = newColors[randomNumber()]
  // }, 20)
  // useFrame(() => (randomColor += newColors[randomNumber()]))

  const [active, setActive] = useState(0)

  // const propss = useSpring({
  //   spring: isBooActivated ? 1 : 0,
  //   config: { mass: 5, tension: 400, friction: 50, precision: 0.0001 }
  // })
  useFrame(() => {
    if (isBooActivated) {
      mesh.current.rotation.z = mesh.current.rotation.y += 0.1
    } else if (active) {
      mesh.current.rotation.z = mesh.current.rotation.y += 0.004
    } else {
      mesh.current.rotation.z = mesh.current.rotation.y += 0.01
    }
  })

  // interpolate values from commong spring
  // const scale = propss.spring.to([0, 1], [1, 1])
  // const rotation = propss.spring.to([0, 1], [0, Math.PI])
  // const color = propss.spring.to([0, 1], ['#32CD32', '#5eff00'])
  // let color
  // useFrame(() => (color = spring.to([0, 1], ['#6246ea', 'blue'])))

  //console.log({ randomColor, randomNumber: randomNumber() })
  //     <mesh ref={mesh} rotation-y={rotation} scale-x={scale} scale-z={scale} onPointerOver={() => setActive(Number(!active))}>

  const ref = useRef()
  const size = useScreenSize()
  const isDesktop = size.device === deviceMap.desktop
  useFrame(({ clock }) => {
    if (isDesktop) {
      ref.current.position.x = ref.current.position.y = ref.current.position.z = Math.cos(clock.getElapsedTime()) * 1.4
    }
    // ref.current.rotation.x = ref.current.rotation.y = ref.current.rotation.z = Math.cos(clock.getElapsedTime()) * 0.1
  })

  return (
    <>
      <Suspense fallback={null}>
        {isDesktop ? (
          <group ref={ref}>
            <Text hAlign="center" position={[3, -11, 20]} children="COME" />
            <Text hAlign="center" position={[13, -11, 20]} children="ON" />
            <Text hAlign="center" position={[20, -11, 20]} children="IN" />
          </group>
        ) : null}
      </Suspense>

      <mesh ref={mesh} onPointerOver={() => setActive(Number(!active))} onPointerOut={() => setActive(Number(!active))}>
        <boxBufferGeometry attach="geometry" args={[3, 3, 3]} />
        <meshStandardMaterial roughness={0.7} attach="material" color={'#32CD32'} />
      </mesh>
    </>
  )
}

const RThreeFApp = () => {
  return (
    <Canvas colorManagement camera={{ position: [-10, 12, -10], fov: 35 }}>
      <ambientLight />
      <pointLight position={[0, 7, -3]} castShadow />
      <Father />
    </Canvas>
  )
}

ReactDOM.render(
  <BrowserRouter>
    <Routes>
      <Route path="/" element={<RThreeFApp />} />
      <Route path="art" element={<Paintings />} />
    </Routes>
  </BrowserRouter>,
  document.getElementById('root')
)
